import React, {
    ReactElement,
    useState,
} from "react";
import {
    ChatSettings,
    ChatSettingsInput,
    FormFieldType,
    FormFieldVisibility,
    GetChannelInvitesDocument,
    Settings,
    useGetAdminChannelInviteQuery,
    useSaveChannelInviteMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import Editor from "../../../../components/agent-v3/editor/Editor";
import { VariablesInput } from "../../../../components/agent-v3/editor/Variables";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import Popover from "../../../../components/popover/Popover";
import Button from "../../../../components/theme/button/Button";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import Text from "../../../../components/theme/text/Text";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting from "../../../../contexts/Routing";
import { ErrorRemote } from "../../../error/Error";
import { DEEP_COMPARER } from "../base";
import { DisplayChatSettings } from "../chat/ChatV3";
import Styles from "./ChannelInvites.module.scss";

const CHANNEL_NAME_VARIABLES: VariablesInput = {
    valueFormat: "fieldId",
    shouldReplaceVariables: true,
    variables: [
        {
            id: "request-fields",
            label: "Request",
            description: "The following fields are available when creating a ticket.",
            input: [
                {
                    id: "COMPANY",
                    name: "Company",
                    description: "Replaces with the name of the company entered",
                    isSystemField: true,
                    type: FormFieldType.Text,
                    visibility: FormFieldVisibility.Reusable,
                },
                {
                    id: "NAME",
                    name: "Name",
                    description: "Replaces with the full name that was entered.",
                    isSystemField: true,
                    type: FormFieldType.Text,
                    visibility: FormFieldVisibility.Reusable,
                },
                {
                    id: "FIRST_NAME",
                    name: "First Name",
                    description: "Replaces with the first name of the user that was entered.",
                    isSystemField: true,
                    type: FormFieldType.Text,
                    visibility: FormFieldVisibility.Reusable,
                },
                {
                    id: "LAST_NAME",
                    name: "Last Name",
                    description: "Replaces with the last name of the user that was entered.",
                    isSystemField: true,
                    type: FormFieldType.Text,
                    visibility: FormFieldVisibility.Reusable,
                },
                {
                    id: "EMAIL",
                    name: "Email",
                    description: "Replaces with the email address entered.",
                    isSystemField: true,
                    type: FormFieldType.Text,
                    visibility: FormFieldVisibility.Reusable,
                },

            ],
        },
    ],
};

function isUrl(value: string | null | undefined) {
    if (!value) {
        return false;
    }
    try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
    } catch {
        return false;
    }
}

export default function ChannelInvite({
    id, settings,
}: {
    id: string;
    settings: Settings | undefined;
}): ReactElement {
    useArticleContext("urn:fq:article:22c4892ab6ad48d5ac58d5a9f72559c4");

    const { data, error, loading: isLoading } = useGetAdminChannelInviteQuery({
        skip: id === "new",
        variables: {
            id,
        },
    });
    const { historyPush, Paths } = useRouting();
    const [save] = useSaveChannelInviteMutation();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .proxy("chatSettings", () => data?.channelInvite?.settings as ChatSettings, DEEP_COMPARER)
            .property("introductionUrl", () => ((isUrl(data?.channelInvite?.introductionTemplate)
                ? data?.channelInvite?.introductionTemplate
                : undefined) ?? ""))
            .property("introductionBody", () => ((!isUrl(data?.channelInvite?.introductionTemplate)
                ? data?.channelInvite?.introductionTemplate
                : undefined) ?? ""))
            .property("channelNameTempate", () => ({
                clean: data?.channelInvite?.channelNameTemplate ?? "",
                html: data?.channelInvite?.channelNameTemplate ?? "",
            }), (left, right) => left?.clean === right?.clean)
            .property("inviteName", () => data?.channelInvite?.name ?? "")
            .property("description", () => data?.channelInvite?.description ?? ""),
        [data?.channelInvite],
    );

    const [isAdvanced, setIsAdvanced] = useState(false);

    const { element } = tracker.useDirtyTracker({
        finishedSavingMessage: "Channel Template saved.",
        onSave: async (__, onDone) => {
            const saved = await save({
                refetchQueries: [
                    { query: GetChannelInvitesDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    invite: {
                        channelNameTemplate: tracker.properties.channelNameTempate.value.clean,
                        name: tracker.properties.inviteName.value as string,
                        settings: cloneWithoutTypename(
                            tracker.properties.chatSettings.serialize() as ChatSettings,
                        ) as ChatSettingsInput,
                        introductionTemplate: tracker.properties.introductionUrl.value
                        || tracker.properties.introductionBody.value,
                        description: tracker.properties.description.value as string,
                    },
                },
            });
            onDone();
            if (id === "new") {
                historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                    setting: "channel-invites",
                    id: saved.data?.saveChannelInvite?.id,
                });
            }
        },
    });

    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <SettingsContainer saveElement={element}>
            <SettingsContainer.Section title="Channel Template">
                <SettingsContainer.Setting
                    title="Name"
                    description="The description of the invite useful to help understand what
                    this invite is used for.  This description is not visibile to the user."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        onChange={properties.description.setValueFromInput}
                        value={properties.description.value}
                        size="small"
                        isRequired
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Invite Heading"
                    description={`This will usually be your company name and is the name that shows
                        up in the channel template invite.
                        On the invite page, this says "Slack Connect with ${properties.inviteName.value || "[Invite Name]"}"`}
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        onChange={properties.inviteName.setValueFromInput}
                        value={properties.inviteName.value}
                        size="small"
                        isRequired
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Channel Name Template"
                    description={`The Channel Name template allows you to specify what the name of the channel will be when it
                        is created in Slack. Because the Channel Template Inviter will fail to create the
                        invite if the channel already exists, it is
                        recommended to use variables in the channel template.`}
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <Editor
                        value={properties.channelNameTempate.value.html}
                        onChange={(html, clean) => properties.channelNameTempate.setValue({
                            clean: clean.replace(/\s/g, ""),
                            html,
                        })}
                        size="small"
                        hasBorder
                        isRequired
                        isSingleLine
                        canFormat={false}
                        variables={CHANNEL_NAME_VARIABLES}
                        placeholder="customer-{{COMPANY}}"
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Introduction Template"
                    description="After customers complete the Channel Template Inviter form,
                            Foqal Agent can automatically send a welcome message in the new channel.
                            You can add a basic text template or use the Slack Block Kit builder for
                            a more interactive introduction."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <div className={Styles["intro-tabs"]}>
                        <Button
                            size="medium"
                            variant="none"
                            className={!isAdvanced ? Styles["intro-selected"] : ""}
                            onClick={() => setIsAdvanced(false)}
                        >
                            Basic
                        </Button>
                        <Button
                            size="medium"
                            variant="none"
                            className={isAdvanced ? Styles["intro-selected"] : ""}
                            onClick={() => setIsAdvanced(true)}
                        >
                            Advanced
                        </Button>
                    </div>
                    {isAdvanced ? (
                        <>
                            <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                                To create an advanced welcome screen, you can use Slack Block Kit builder.
                                This is a tool created by Slack to help you visualize an interactive Slack
                                message. See
                                {" "}
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-default"
                                    href="https://www.foqal.io/landing/slack-connect/default"
                                >
                                    our example here
                                </a>
                                {" "}
                                for a good starting point. Once you are done,
                                simply copy and paste the entire URL into the box below.
                            </Text>
                            <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                                You can customize this message with the information entered by the customer.
                                {" "}
                                <Popover
                                    variant="primary"
                                    size="md"
                                    iconSize="sm"
                                    title="Variables"
                                    text={(
                                        <Text textVariant="body-2-regular" colorVariant="text-link">
                                            Available Variables
                                        </Text>
                                    )}
                                >
                                    <>
                                        <p>
                                            When displaying the Channel Creator, Foqal asks the user
                                            for their name, email, and company name. When creating the
                                            channel, by entering the variables below you can customize
                                            the name of the channel created and the introduction message displayed.
                                        </p>
                                        <p>
                                            You can use the following variables in the channel name template,
                                            the introduction message, or in the body of Slack Block Kit Builder:
                                        </p>
                                        <ul>
                                            <li>
                                                <code>&#123;&#123;COMPANY&#125;&#125;</code>
                                                {" "}
                                                - Replaces with the name of the company entered.
                                            </li>
                                            <li>
                                                <code>&#123;&#123;NAME&#125;&#125;</code>
                                                {" "}
                                                - Replaces with the full name that was entered.
                                            </li>
                                            <li>
                                                <code>&#123;&#123;FIRST_NAME&#125;&#125;</code>
                                                {" "}
                                                - Replaces with the parsed first name entered.
                                            </li>
                                            <li>
                                                <code>&#123;&#123;LAST_NAME&#125;&#125;</code>
                                                {" "}
                                                - Replaces with the parsed last name entered.
                                            </li>
                                            <li>
                                                <code>&#123;&#123;EMAIL&#125;&#125;</code>
                                                {" "}
                                                - Replaces with the email address entered.
                                            </li>

                                        </ul>
                                    </>
                                </Popover>
                            </Text>
                            <FormGroup
                                onChange={properties.introductionUrl.setValueFromInput}
                                value={properties.introductionUrl.value}
                                size="small"
                                type="url"
                            />
                        </>
                    ) : (
                        <>
                            <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                                Enter the welcome message you would like the customer to
                                see as soon as they accept the Slack Connect Channel invite.
                                You can customize this message with the information entered
                                by the customer at the time they are filling out the form.
                                Available variables
                            </Text>
                            <Editor
                                value={properties.introductionBody.value}
                                onChange={properties.introductionBody.setValue}
                                size="medium"
                                hasBorder
                                placeholder="Welcome to the channel"
                                variables={CHANNEL_NAME_VARIABLES}
                            />
                        </>
                    )}
                </SettingsContainer.Setting>

            </SettingsContainer.Section>

            <DisplayChatSettings
                chatSettings={properties.chatSettings}
                settings={settings}
                shouldAllowSlack
                isLoading={isLoading}
            />
        </SettingsContainer>

    );
}
